import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://vizard.ai/pricing";
const CHECKED = "2026-10-08";
const VERIFIED = "2026-10-09";
const TECHSY = "https://techsy.io/en/blog/opusclip-vs-vizard";

export const VIZARD: CompetitorRecord = {
  slug: "vizard",
  name: "Vizard",
  aliases: ["Vizard AI", "vizard.ai"],
  url: "https://vizard.ai",
  pricingUrl: PRICING,
  category: "clipper",
  positioning: "Turn your long video into viral clips with AI magic.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "60 credits per month, 1 credit per minute of video",
      includes: [
        "Export at 720p, 10-minute export length",
        "Uploads up to 60 minutes and 1 GB",
        "3-day storage",
        "1 social account",
        "Full video editor and API access",
        "Watermark on exports",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Creator",
      monthly: 29,
      annualPerMonth: 14.5,
      annualTotal: 174,
      metered: "From 600 credits per month, 1 credit per minute of video; annual tiers from 7,200 to 74,400 credits a year",
      includes: [
        "No watermark",
        "Export at 4K",
        "Uploads up to 600 minutes and 30 GB",
        "Schedule posts to 6 social accounts",
        "100 GB storage",
        "API at 3 calls a minute, 20 an hour",
      ],
      sourceUrl: PRICING,
      checkedAt: VERIFIED,
      note: "Read in a real browser on Oct 9, 2026: $29 monthly, $14.50 a month on the yearly plan, $174 billed yearly.",
    },
    {
      name: "Business",
      monthly: 39,
      annualPerMonth: 19.5,
      annualTotal: 234,
      metered: "From 600 credits per month with custom credit pools, 1 credit per minute of video",
      includes: [
        "Shared workspace; extra seats $10 a month each on monthly billing, $5 on yearly",
        "Schedule posts to 20 social accounts",
        "Brand kit",
        "Unlimited storage",
        "API at 10 calls a minute, 60 an hour",
      ],
      sourceUrl: PRICING,
      checkedAt: VERIFIED,
      note: "Read in a real browser on Oct 9, 2026: $39 monthly, $19.50 a month on the yearly plan, $234 billed yearly.",
    },
  ],
  entryPlanName: "Creator",
  entryPlanIncludes: "600 credits, which is 600 minutes of uploaded video, no watermark, 4K export, scheduler to 6 accounts",
  freeTier: "60 credits a month at 720p with a watermark, 10-minute exports, 60-minute uploads, 3-day storage, full editor and API",
  trial: "Not stated on the pricing page",
  meteringUnit: "1 credit per minute of video uploaded, whatever you keep",
  exportResolution: "720p on Free, 4K on Creator and Business",
  features: {
    longToShort: { v: "yes", note: "30+ clips in one click" },
    momentRanking: {
      v: "n/a",
      note: "No virality score on their pages",
      source: TECHSY,
    },
    reframing: { v: "yes", note: "Centers key subjects for TikTok, Reels, Shorts" },
    maxSource: { v: "text", note: "60 minutes on Free, 600 minutes paid" },
    layouts: { v: "n/a", note: "Not enumerated on the pricing page" },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "no", note: "Not advertised" },
    voiceover: { v: "n/a", note: "Not on the homepage or pricing page" },
    voiceCloning: { v: "n/a" },
    avatars: { v: "n/a" },
    timelineEditor: { v: "yes", note: "Full editor access on Free too" },
    captionStyles: { v: "text", note: "22 templates in Vizard Agent" },
    captionLanguages: { v: "text", note: "100+ translation languages" },
    bRoll: { v: "yes", note: "AI B-roll" },
    fourK: { v: "yes", note: "Paid plans; Free exports 720p" },
    scheduling: { v: "yes", note: "6 accounts on Creator, 20 on Business" },
    analytics: { v: "n/a", note: "Not visible on the homepage" },
    socialTracker: { v: "no" },
    api: { v: "yes", note: "Every tier including Free, rate-limited" },
    webhooks: { v: "n/a" },
    web: { v: "yes" },
    desktop: { v: "no" },
    mobile: { v: "n/a", note: "iOS app reported by third parties only" },
    teamSeats: { v: "partial", note: "Business only, $5 to $10 per seat per month" },
    freeTier: { v: "yes", note: "60 credits, 720p, watermark, 3-day storage" },
    trial: { v: "n/a", note: "Not stated on the pricing page" },
    metering: { v: "text", note: "1 credit per source minute" },
    rollover: {
      v: "partial",
      note: "Paid plans, per a third-party guide",
      source: "https://www.aitoolcurator.com/learn/vizard-ai-how-to-guide/how-credits-work/",
    },
    refunds: { v: "text", note: "Not stated on the pricing page" },
    cancelInApp: { v: "n/a", note: "Mechanism not described; plan expires at cycle end" },
  },
  billingTerms: {
    refunds: "No refund terms appear on the pricing page as of Oct 8, 2026.",
    cancel:
      "The pricing page FAQ says that once you cancel, the plan will expire at the end of your billing cycle. It does not describe where to cancel.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "any unused credits are forfeited upon cancellation with no refund or carryover",
      date: "2026-03-02",
      url: "https://www.trustpilot.com/review/vizard.ai",
      evidences: "rollover",
    },
  ],
  verification: {
    method: "browser",
    date: VERIFIED,
    note: "The pricing page renders prices client-side, so we read it in a real browser on Oct 9, 2026, on both toggles: Creator $29 monthly or $14.50 a month billed yearly ($174), Business $39 monthly or $19.50 a month billed yearly ($234), extra seats $10 a month monthly or $5 yearly, Creator 600 credits a month with a slider to 6,200. Free plan limits (60 credits, 1 credit per minute, 720p, 10-minute exports, 60-minute uploads) were read from the same page on Oct 8 and Oct 9.",
  },
  seoPattern: "Blog listicles ranking Vizard first; no /vs/ or /alternatives/ pages, no page about Crayo",
};
